import { useState, useCallback } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";

/**
 * CollapsibleCard — generic wrapper for the viewer-tab side cards.
 * - `title` + `icon` render the header label (left side, click toggles).
 * - `actions` slot stays on the right (used for Edit/Save/Cancel toolbars).
 * - State persisted in `localStorage` under `storageKey` (when provided)
 *   so the user's expand/collapse choice survives reloads.
 */
export default function CollapsibleCard({
  title,
  icon,
  actions,
  children,
  defaultOpen = true,
  storageKey,
  className = "",
  titleClassName = "",
  testid,
}) {
  const initial = useCallback(() => {
    if (!storageKey) return defaultOpen;
    try {
      const v = window.localStorage.getItem(`mp_collapse_${storageKey}`);
      if (v === "0") return false;
      if (v === "1") return true;
    } catch (_e) { /* ignore */ }
    return defaultOpen;
  }, [storageKey, defaultOpen]);
  const [open, setOpen] = useState(initial);

  const toggle = () => {
    setOpen((v) => {
      const nv = !v;
      if (storageKey) {
        try { window.localStorage.setItem(`mp_collapse_${storageKey}`, nv ? "1" : "0"); } catch (_e) { /* ignore */ }
      }
      return nv;
    });
  };

  return (
    <div className={`tech-card ${open ? "p-5" : "px-5 py-3"} ${className}`} data-testid={testid}>
      <div className={`flex items-center justify-between ${open ? "mb-3" : ""}`}>
        <button
          type="button"
          onClick={toggle}
          aria-expanded={open}
          className={`flex items-center gap-2 ${titleClassName || "text-slate-300"} hover:opacity-80 transition-opacity`}
          data-testid={testid ? `${testid}-toggle` : undefined}
        >
          {open ? <ChevronDown size={14} className="opacity-70"/> : <ChevronRight size={14} className="opacity-70"/>}
          {icon}
          <div className="label-tech">{title}</div>
        </button>
        {open && actions ? <div onClick={(e) => e.stopPropagation()}>{actions}</div> : null}
      </div>
      {open && children}
    </div>
  );
}
